import {
  ComponentPropsWithoutRef,
  useState,
  useRef,
  useLayoutEffect,
} from "react";
import { Chip, ChipProps } from "../../../../components/Chip";
import cx from "classnames";
import styles from "./TruncateChips.module.scss";

/**
 * Props for the TruncateChips component
 */
export type TruncateChipsProps = ComponentPropsWithoutRef<"div"> & {
  /**
   * Array of chip items to display
   */
  items: ChipProps[];
};

/**
 * Optimized TruncateChips component using measurement-based calculation.
 *
 * Features:
 * - Uses visible measurement chips to determine actual layout
 * - Responsive to container width changes
 * - Supports custom gap between chips
 * - Clean, predictable behavior
 *
 * @example
 * <TruncateChips
 *   items={[
 *     { label: "React", onClose: () => {} },
 *     { label: "TypeScript", onClose: () => {} },
 *     { label: "JavaScript", onClose: () => {} }
 *   ]}
 * />
 */
export const TruncateChips = ({
  items = [],
  className,
  ...rest
}: TruncateChipsProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chipsContainerRef = useRef<HTMLDivElement>(null);
  const [hiddenCount, setHiddenCount] = useState(0);
  const hiddenChips = useRef<{ el: HTMLElement; rect: DOMRect }[]>([]);
  const plusChipRef = useRef<HTMLSpanElement>(null);
  const [plusChipWidth, setPlusChipWidth] = useState(0);

  useLayoutEffect(() => {
    if (!chipsContainerRef.current) return;
    let totalHiddenCount = 0;
    const observer = new IntersectionObserver(
      (entries) => {
        const isResizing = document.querySelector(
          '[aria-label="Resize column"][aria-pressed="true"]',
        );
        if (isResizing) return;

        // Update the state of each element that was observed
        entries.forEach((entry) => {
          const target = entry.target as HTMLElement;

          if (entry.isIntersecting) {
            // Element is visible, keep it visible
            target.style.display = "flex";
            target.style.visibility = "visible";
            totalHiddenCount = Math.max(0, totalHiddenCount - 1);
          }

          if (!entry.isIntersecting) {
            // Element is outside the container, hide it
            hiddenChips.current.push({
              el: target,
              rect: target.getBoundingClientRect(),
            });
            target.style.visibility = "hidden";
            target.style.display = "none";
            totalHiddenCount++;
          }
        });

        setHiddenCount(totalHiddenCount);
      },
      {
        root: containerRef.current,
        rootMargin: "0px",
        threshold: 1,
      },
    );

    const children = Array.from(chipsContainerRef.current.children);
    children.forEach((child) => {
      observer.observe(child as HTMLElement);
    });

    // Cleanup observer on unmount or dependency change
    return () => {
      observer.disconnect();
    };
  }, [items]); // Re-run when items change

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[entries.length - 1];
      if (entry) {
        if (hiddenCount > 0 && containerRef.current) {
          const plusChipRect = plusChipRef.current?.getBoundingClientRect();
          setPlusChipWidth(plusChipRect?.width ?? 0);
          const containerRect = containerRef.current.getBoundingClientRect();
          const chipRect =
            hiddenChips.current[hiddenChips.current.length - 1].rect;

          const fitsHorizontally =
            chipRect.width <= containerRect.width &&
            chipRect.left >= containerRect.left &&
            chipRect.right <= containerRect.right;

          const fitsVertically =
            chipRect.height <= containerRect.height &&
            chipRect.top >= containerRect.top &&
            chipRect.bottom <= containerRect.bottom;

          const hasEnoughSpace = fitsHorizontally && fitsVertically;

          hiddenChips.current[hiddenCount - 1].el.style.display = hasEnoughSpace
            ? "flex"
            : "none";

          if (hasEnoughSpace) {
            hiddenChips.current.pop();
          }
        }
      }
    });

    observer.observe(containerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [hiddenCount]);

  return (
    <div
      ref={containerRef}
      className={cx(styles["truncate-chips"], className)}
      style={{
        marginInlineEnd: plusChipWidth,
      }}
      {...rest}
    >
      <div ref={chipsContainerRef} style={{ display: "contents" }}>
        {/* Visible chips container */}
        {items.map((chip, index) => (
          <Chip key={index} {...chip} style={{ visibility: "hidden" }} />
        ))}
      </div>

      {/* Visible plus chip */}
      {hiddenCount > 0 && <Chip label={`+${hiddenCount}`} ref={plusChipRef} />}
    </div>
  );
};
